import React from "react";
import Dialog from "@mui/material/Dialog";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Upload from "./Upload";
import Loading from './Loading';
import { apiPost } from "../api";

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
  }
  return null;
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const XIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M18 6L6 18M6 6l12 12"/>
  </svg>
);
const FolderIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
  </svg>
);
const LockIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

export default function UploadDocuments(props) {
  const [activeSection, setActiveSection] = React.useState(null); // 'tecnica' | 'legal' | 'cao'
  // El estado de la solicitud ya viene en props: calcular que secciones estan
  // habilitadas durante el render evita el parpadeo de abrir el modal con todo
  // bloqueado y desbloquearlo un frame despues, cuando corria el useEffect.
  const estadoSolicitud = props.clickRow?.solicitud?.attributes?.estado;
  const stateLegal = React.useMemo(() => (
    estadoSolicitud === "Esperando documentación legal" ||
    estadoSolicitud === "En evaluación legal" ||
    estadoSolicitud === "Esperando CAO" ||
    estadoSolicitud === "CAO presentado"
  ) ? "1" : "0.5", [estadoSolicitud]);
  const stateCAO = React.useMemo(() => (
    estadoSolicitud === "Esperando CAO" ||
    estadoSolicitud === "CAO presentado"
  ) ? "1" : "0.5", [estadoSolicitud]);
  const [files, setFiles] = React.useState([]);
  const [uploadError, setUploadError] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [ver, setVer] = React.useState(false);
  const [loadingFiles, setLoadingFiles] = React.useState(false);
  const [displayUploadBtn, setDisplayUploadBtn] = React.useState("block");

  // rawBody: va como FormData, sin Content-Type (lo pone el browser con el
  // boundary). apiPost propaga el error con el mensaje que manda la API, en vez
  // de devolver false y perderlo — antes solo se contemplaba el error de tamano
  // o tipo de archivo y cualquier otro fallaba en silencio.
  const addAttachments = (attachments) =>
    apiPost(process.env.REACT_APP_API_ADD_ATTACHMENTS, attachments, { rawBody: true });

  const handleClose = () => {
    props.setUpload(false);
    setActiveSection(null);
    setVer(false);
    setDisplayUploadBtn("block");
  };

  const handleSubmit = () => {
    if (!window.confirm("¿Seguro desea presentar la documentación? No podrá volver a cargar una vez presentado.")) return;
    if (files.length > 0) {
      setLoadingFiles(true);
      const type = activeSection === 'tecnica' ? "Técnica" : activeSection === 'legal' ? "Legal" : activeSection === 'cao' ? "CAO" : null;
      const formdata = new FormData();
      formdata.append("tipo_documentacion", type);
      formdata.append("id_solicitud", props.clickRow.solicitud.attributes.id);
      files.forEach(f => formdata.append("attachments", f, f.name));
      addAttachments(formdata).then(x => {
        setLoadingFiles(false);
        if (x) {
          setVer(1);
          props.setNuevaSolicitudes(Math.random());
          alert("Su solicitud está siendo evaluada. Le llegará un mail con los próximos pasos.");
        }
      }).catch(err => {
        setLoadingFiles(false);
        if (err?.sesionVencida) return;
        // El archivo rechazado por tamano/tipo ya tiene su propio aviso.
        if (err?.message?.includes("File size or type not supported")) {
          setUploadError(true);
          return;
        }
        setSubmitError(err?.message || "No se pudo cargar la documentación.");
      });
    }
  };

  React.useEffect(() => {
    if (!estadoSolicitud) {
      setVer(false);
      return;
    }
    setVer(
      estadoSolicitud === "En evaluación técnica" ||
      estadoSolicitud === "En evaluación legal" ||
      estadoSolicitud === "CAO presentado"
        ? 2
        : false
    );
  }, [props.upload, estadoSolicitud]);

  const docRailItems = [
    {
      key: 'tecnica',
      label: 'Documentación técnica',
      desc: 'Memoria, planos, ficha técnica',
      locked: false,
      badge: null,
    },
    {
      key: 'legal',
      label: 'Documentación legal',
      desc: 'Documentación legal requerida',
      locked: stateLegal !== "1",
      badge: stateLegal !== "1" ? 'locked' : null,
    },
    {
      key: 'cao',
      label: 'Documentación CAO',
      desc: 'Constancia de aviso de obra',
      locked: stateCAO !== "1",
      badge: stateCAO !== "1" ? 'locked' : null,
    },
  ];

  const getDocTitle = () => {
    if (activeSection === 'tecnica') return 'Documentación técnica';
    if (activeSection === 'legal') return 'Documentación legal';
    if (activeSection === 'cao') return 'Documentación CAO';
    return 'Carga de documentos';
  };

  return (
    <Dialog
      open={props.upload}
      onClose={(_, reason) => reason !== 'backdropClick' && handleClose()}
      // Sin esto MUI bloquea el scroll del body y compensa con padding-right:
      // la pagina de atras se corre para el costado al abrir el modal.
      disableScrollLock
      PaperProps={{
        style: {
          width: '900px', maxWidth: '95vw', borderRadius: 13,
          // Altura fija (no maxHeight): si dependiera del contenido, el modal
          // saltaria de tamano al pasar del placeholder corto a una seccion con
          // el texto legal largo. El scroll es de todo el cuerpo, footer
          // incluido: los botones quedan al final del contenido, no fijos.
          height: '90vh', maxHeight: 720, display: 'flex', flexDirection: 'column',
        },
      }}
    >
      {loadingFiles && <Loading color="#00000014" height="100%" width="100%" />}

      {/* Modal header */}
      <div className="ds-modal-head">
        <div>
          <div className="ds-modal-title">{getDocTitle()}</div>
          {props.clickRow && (
            <div className="ds-modal-sub">
              Solicitud {props.clickRow.solicitud.attributes.id} · {props.clickRow.solicitud.attributes.denominacion}
            </div>
          )}
        </div>
        <div className="ds-modal-x" onClick={handleClose}><XIcon /></div>
      </div>

      {/* Two-panel body */}
      <div className="ds-upload-layout">
        {/* Left rail */}
        <div className="ds-upload-rail">
          {docRailItems.map(item => (
            <div
              key={item.key}
              className={`ds-rail-item${activeSection === item.key ? ' active' : ''}${item.locked ? ' locked' : ''}`}
              onClick={() => {
                if (!item.locked) {
                  setActiveSection(item.key);
                  setVer(false);
                  setDisplayUploadBtn("block");
                }
              }}
            >
              <div className="ds-rail-icon">{item.locked ? <LockIcon /> : <FolderIcon />}</div>
              <div style={{ flex: 1 }}>
                <div className="ds-rail-title">{item.label}</div>
                <div className="ds-rail-desc">{item.desc}</div>
                {item.locked && (
                  <div style={{ marginTop: 6 }}>
                    <span className="ds-badge draft" style={{ fontSize: 10 }}>
                      <span className="dot" />Pendiente habilitación
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Right content */}
        <div className="ds-upload-content">
          {!activeSection ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 12, color: 'var(--ink-4)', paddingTop: 40 }}>
              {/* Los SVG locales solo declaran viewBox: sin un tamano explicito
                  el icono se estira a todo el alto del contenedor flex. */}
              <FolderIcon style={{ width: 44, height: 44, flex: 'none', opacity: 0.6 }} />
              <span style={{ fontSize: 14 }}>Seleccioná una sección de la izquierda para comenzar.</span>
            </div>
          ) : (
            <Upload
              // key por seccion: fuerza remontar Upload al cambiar de
              // documentacion, asi su estado inicial se recalcula en vez de
              // arrastrar el de la seccion anterior y corregirse un frame
              // despues (era lo que hacia aparecer y ocultar el texto legal).
              key={activeSection}
              ver={ver} setVer={setVer}
              uploadError={uploadError} setUploadError={setUploadError}
              clickRow={props.clickRow}
              type={activeSection === 'tecnica' ? "Técnica" : activeSection === 'legal' ? "Legal" : "CAO"}
              setDisplayButtonHook={setDisplayUploadBtn}
              setFiles={setFiles}
            />
          )}

          {/* Footer dentro de la columna de contenido: asi scrollea junto con
              la documentacion en vez de quedar fijo abajo. */}
          <div className="ds-modal-foot ds-modal-foot--inline">
            <button className="ds-btn ds-btn-ghost" onClick={handleClose}>Cancelar</button>
            {activeSection && displayUploadBtn === "block" && (
              <button className="ds-btn ds-btn-primary" onClick={handleSubmit} style={{ display: displayUploadBtn }}>
                Guardar documentación
              </button>
            )}
          </div>
        </div>
      </div>

      <Snackbar open={uploadError} autoHideDuration={6000} onClose={() => setUploadError(false)}>
        <Alert severity="error" sx={{ width: '100%' }}>
          Error al cargar el archivo. Tamaño o tipo no admitido.
        </Alert>
      </Snackbar>

      {/* Sin autoHideDuration: el motivo del rechazo (por ejemplo, que la
          solicitud ya no este esperando esa documentacion) tiene que quedar
          hasta que el usuario lo cierre. */}
      <Snackbar open={!!submitError} onClose={() => setSubmitError('')}>
        <Alert severity="error" onClose={() => setSubmitError('')} sx={{ width: '100%' }}>
          {submitError}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
